import { EntitiesStatus } from 'common/types/Entities';
import {
    AclEntriesInactiveStatuses,
    AclEntriesStatusTooltipDateKeys,
    AclEntriesStatusTooltipDates,
    AclEntriesStatusTooltipProps } from 'common/types/TypeDefs';
import { isActiveDatesPair } from 'common/utils/dateHelpers';
import { AclEntriesPage } from 'features/aclEntries/aclEntriesTypeDefs';

const USER_ACL_ENTRIES_STATUS_TOOLTIP_BASE_ENTITIES = ['User', 'License', 'Acl'] as const;
const USERGROUP_ACL_ENTRIES_STATUS_TOOLTIP_BASE_ENTITIES = ['License', 'Acl'] as const;

const AclEntriesTooltipBaseEntitiesByAclPage:
    Partial<Record<
        AclEntriesPage,
        typeof USER_ACL_ENTRIES_STATUS_TOOLTIP_BASE_ENTITIES |
        typeof USERGROUP_ACL_ENTRIES_STATUS_TOOLTIP_BASE_ENTITIES
    >> = {
        [AclEntriesPage.TenantUser]: USER_ACL_ENTRIES_STATUS_TOOLTIP_BASE_ENTITIES,
        [AclEntriesPage.UserProfile]: USER_ACL_ENTRIES_STATUS_TOOLTIP_BASE_ENTITIES,
        [AclEntriesPage.TenantUserGroup]: USERGROUP_ACL_ENTRIES_STATUS_TOOLTIP_BASE_ENTITIES
    };

export const calculateAclEntriesStatus = (
    tooltipDates: AclEntriesStatusTooltipDates,
    currentAclPage: AclEntriesPage
) => {
    const currentAclEntriesBaseEntities = AclEntriesTooltipBaseEntitiesByAclPage[currentAclPage];
    const tooltipProps: AclEntriesStatusTooltipProps = {
        ...tooltipDates,
        UserInactive: false,
        LicenseInactive: false,
        AclInactive: false,
    };

    let status = EntitiesStatus.ACTIVE;

    if (currentAclEntriesBaseEntities) {
        for (const entity of currentAclEntriesBaseEntities) {
            const currentEntityStartDateKey = `${entity}StartDate` as AclEntriesStatusTooltipDateKeys;
            const currentEntityEndDateKey = `${entity}EndDate` as AclEntriesStatusTooltipDateKeys;
            const currentEntityInactiveKey = `${entity}Inactive` as AclEntriesInactiveStatuses;
            const isInactiveStatus = !isActiveDatesPair(tooltipProps[currentEntityStartDateKey], tooltipProps[currentEntityEndDateKey]);

            if (isInactiveStatus) {
                tooltipProps[currentEntityInactiveKey] = true;
                status = EntitiesStatus.INACTIVE;
            }
        }
    }

    return {
        tooltipProps,
        status
    };
};

export type HasStartAndEndDate = {
    startDate?: string;
    endDate?: string;
}

export const _getAclEnriesStatusTooltipDates = <
    T extends HasStartAndEndDate | undefined,
    U extends HasStartAndEndDate | undefined,
    V extends HasStartAndEndDate | undefined
>(userAclEntry?: T, userTenantMembership?: U, licenseByAclEntry?: V): AclEntriesStatusTooltipDates => {
    const AclStartDate = userAclEntry?.startDate ?? '';
    const AclEndDate = userAclEntry?.endDate ?? '';
    const LicenseStartDate = licenseByAclEntry?.startDate ?? '';
    const LicenseEndDate = licenseByAclEntry?.endDate ?? '';
    const UserStartDate = userTenantMembership?.startDate ?? '';
    const UserEndDate = userTenantMembership?.endDate ?? '';

    return {
        UserStartDate,
        UserEndDate,
        AclStartDate,
        AclEndDate,
        LicenseStartDate,
        LicenseEndDate
    };
};

export const _removeAclEntriesStatusTooltipEmptyDates = (
    tooltipDates: AclEntriesStatusTooltipDates
): AclEntriesStatusTooltipDates => {
    const newObj: AclEntriesStatusTooltipDates = { ...tooltipDates };

    for (const key in newObj) {
        if (newObj[key as keyof AclEntriesStatusTooltipDates] === '') {
            delete newObj[key as keyof AclEntriesStatusTooltipDates];
        }
    }

    return newObj;
};
